import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_API_URL || 'https://api.guardianbazaar.com'}/api`;

type Theme = 'dark' | 'light';

interface ThemeContextType {
  theme:      Theme;
  toggleTheme: () => void;
  setTheme:   (t: Theme) => void;
}

const ThemeContext = createContext<ThemeContextType | null>(null);

export function ThemeProvider({ children }: { children: React.ReactNode }) {
  const [theme, setThemeState] = useState<Theme>('dark');

  // Apply theme to document
  const applyTheme = (t: Theme) => {
    document.documentElement.setAttribute('data-theme', t);
  };

  // Load theme from DB on mount
  useEffect(() => {
    const token = localStorage.getItem('guardianbazaar_token');
    if (!token) return;
    axios.get(`${API}/users/preferences`)
      .then(res => {
        const t = res.data.theme as Theme || 'dark';
        setThemeState(t);
        applyTheme(t);
      })
      .catch(() => applyTheme('dark'));
  }, []);

  const setTheme = useCallback((t: Theme) => {
    setThemeState(t);
    applyTheme(t);
    // Persist to DB silently
    axios.put(`${API}/users/preferences`, { theme: t }).catch(console.error);
  }, []);

  const toggleTheme = useCallback(() => {
    setTheme(theme === 'dark' ? 'light' : 'dark');
  }, [theme, setTheme]);

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme, setTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const ctx = useContext(ThemeContext);
  if (!ctx) throw new Error('useTheme must be used within ThemeProvider');
  return ctx;
}
